import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../../hooks/usePageTitle";
import adminFetch from "../../utils/adminFetch";

// ── Helpers ───────────────────────────────────────────────────────────

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatSubType(sub) {
  return (sub || "—")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getAdminPayload() {
  const token = localStorage.getItem("paladin_admin_token");
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

// ── Config maps ───────────────────────────────────────────────────────
const TYPE_CONFIG = {
  quote:             { label: "Quote",        color: "#002DB5", bg: "rgba(0,45,181,0.09)",  path: "/admin/quotes" },
  consultation:      { label: "Consultation", color: "#012E72", bg: "rgba(1,46,114,0.09)",  path: "/admin/consultations" },
  contact:           { label: "Contact",      color: "#065F46", bg: "rgba(6,95,70,0.09)",   path: "/admin/contacts" },
  "service-request": { label: "Service",      color: "#92400E", bg: "rgba(146,64,14,0.09)", path: "/admin/service-requests" },
};

const STATUS_CONFIG = {
  pending:       { label: "Pending",     color: "#92400E", bg: "#FEF3C7" },
  unread:        { label: "Unread",      color: "#92400E", bg: "#FEF3C7" },
  "in-progress": { label: "In Progress", color: "#1D4ED8", bg: "#DBEAFE" },
  contacted:     { label: "Contacted",   color: "#1D4ED8", bg: "#DBEAFE" },
  read:          { label: "Read",        color: "#1D4ED8", bg: "#DBEAFE" },
  reviewed:      { label: "Reviewed",    color: "#065F46", bg: "#D1FAE5" },
  quoted:        { label: "Quoted",      color: "#065F46", bg: "#D1FAE5" },
  completed:     { label: "Completed",   color: "#065F46", bg: "#D1FAE5" },
  replied:       { label: "Replied",     color: "#065F46", bg: "#D1FAE5" },
  closed:        { label: "Closed",      color: "#6B7280", bg: "#F3F4F6" },
};

const SERIES_CONFIG = [
  { key: "quotes",          label: "Quotes",           color: "#2563EB" },
  { key: "consultations",   label: "Consultations",    color: "#7C3AED" },
  { key: "contacts",        label: "Messages",         color: "#059669" },
  { key: "serviceRequests", label: "Service Requests", color: "#D97706" },
];

// ── Animated Counter ──────────────────────────────────────────────────
function AnimatedNumber({ value, duration = 800 }) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!value) return;
    const start = performance.now();
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setDisplay(Math.round(eased * value));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  return <>{display}</>;
}

// ── Badges ────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "#6B7280", bg: "#F3F4F6" };
  return (
    <span
      className="inline-block font-cinzel uppercase px-2.5 py-1 rounded-full text-[9px] tracking-widest transition-transform duration-150 hover:scale-105"
      style={{ color: cfg.color, backgroundColor: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
}

function TypeBadge({ type }) {
  const cfg = TYPE_CONFIG[type] || { label: type, color: "#6B7280", bg: "#F3F4F6" };
  return (
    <span
      className="inline-block font-cinzel uppercase px-2.5 py-1 rounded-full text-[9px] tracking-widest transition-transform duration-150 hover:scale-105"
      style={{ color: cfg.color, backgroundColor: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────
function StatCard({ label, total, pending, pendingLabel, icon, color, bg, onClick, delay = 0 }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <button
      onClick={onClick}
      className={`group relative bg-white rounded-2xl p-6 flex flex-col gap-4 text-left w-full overflow-hidden
        border border-blue-dark border-opacity-[0.08]
        hover:shadow-lg hover:-translate-y-1 hover:border-opacity-20
        transition-all duration-300 ease-out
        ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
      style={{ transitionDelay: visible ? "0ms" : `${delay}ms` }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out"
        style={{ backgroundColor: color }}
      />
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"
        style={{ background: `radial-gradient(ellipse at top left, ${bg} 0%, transparent 70%)` }}
      />
      <div className="relative flex items-start justify-between">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
          style={{ backgroundColor: bg, color }}
        >
          {icon}
        </div>
        {pending > 0 && (
          <span className="font-cinzel uppercase px-2 py-0.5 rounded-full text-[8px] tracking-widest text-amber-800 bg-amber-100 animate-pulse-soft">
            {pending} {pendingLabel}
          </span>
        )}
      </div>
      <div className="relative">
        <p className="font-cinzel font-bold text-blue-dark group-hover:text-blue-primary transition-colors duration-200 leading-none"
          style={{ fontSize: "32px" }}>
          <AnimatedNumber value={total} />
        </p>
        <p className="font-cinzel uppercase text-gray-400 mt-1.5 text-[9px] tracking-[0.18em]">
          {label}
        </p>
      </div>
    </button>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────
function Spinner() {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-4">
      <svg viewBox="0 0 80 80" width="64" height="64" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id="top-clip"><rect x="20" y="10" width="40" height="27" /></clipPath>
          <clipPath id="bot-clip"><rect x="20" y="43" width="40" height="27" /></clipPath>
        </defs>
        <polygon points="18,8 62,8 40,37" fill="none" stroke="#012E72" strokeWidth="2.2" opacity="0.18"/>
        <polygon points="18,72 62,72 40,43" fill="none" stroke="#012E72" strokeWidth="2.2" opacity="0.18"/>
        <rect x="20" y="10" width="40" height="27" fill="#012E72" opacity="0.55" clipPath="url(#top-clip)">
          <animate attributeName="y" from="10" to="37" dur="1.6s" repeatCount="indefinite" />
          <animate attributeName="height" from="27" to="0" dur="1.6s" repeatCount="indefinite" />
        </rect>
        <circle cx="40" cy="40" r="1.4" fill="#002DB5" opacity="0.7">
          <animate attributeName="cy" values="37;43" dur="0.18s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;0.9;0" dur="0.18s" repeatCount="indefinite" />
        </circle>
        <rect x="20" y="70" width="40" height="0" fill="#012E72" opacity="0.55" clipPath="url(#bot-clip)">
          <animate attributeName="y" from="70" to="43" dur="1.6s" repeatCount="indefinite" />
          <animate attributeName="height" from="0" to="27" dur="1.6s" repeatCount="indefinite" />
        </rect>
        <polygon points="18,8 62,8 40,37" fill="none" stroke="#012E72" strokeWidth="2.2"/>
        <polygon points="18,72 62,72 40,43" fill="none" stroke="#012E72" strokeWidth="2.2"/>
      </svg>
      <p className="font-cinzel uppercase text-[#012E72] opacity-40 tracking-widest text-[9px]">
        Loading…
      </p>
    </div>
  );
}

// ── Trend Chart (pure SVG, no deps) ──────────────────────────────────
function TrendChart({ labels, series, loading }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const svgRef = useRef(null);

  const VW = 800, VH = 200;
  const PAD = { top: 16, right: 16, bottom: 34, left: 42 };
  const plotW = VW - PAD.left - PAD.right;
  const plotH = VH - PAD.top - PAD.bottom;
  const n = labels ? labels.length : 0;

  const allValues = SERIES_CONFIG.flatMap(({ key }) => (series?.[key] || []));
  const rawMax = Math.max(...allValues, 1);
  const yMax = Math.ceil(rawMax * 1.25) || 1;

  const xOf = (i) => PAD.left + (i / Math.max(n - 1, 1)) * plotW;
  const yOf = (v) => PAD.top + plotH - (v / yMax) * plotH;

  function pathD(values) {
    if (!values || values.length === 0) return "";
    return values.map((v, i) => {
      const x = xOf(i), y = yOf(v);
      if (i === 0) return `M ${x.toFixed(1)},${y.toFixed(1)}`;
      const x0 = xOf(i - 1), y0 = yOf(values[i - 1]);
      const cpx = ((x0 + x) / 2).toFixed(1);
      return `C ${cpx},${y0.toFixed(1)} ${cpx},${y.toFixed(1)} ${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
  }

  function areaD(values) {
    if (!values || values.length === 0) return "";
    const line = pathD(values);
    const x0 = xOf(0).toFixed(1), xN = xOf(n - 1).toFixed(1);
    const yB = (PAD.top + plotH).toFixed(1);
    return `${line} L ${xN},${yB} L ${x0},${yB} Z`;
  }

  const handleMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg || n === 0) return;
    const rect = svg.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * VW;
    const raw = ((svgX - PAD.left) / plotW) * (n - 1);
    setHoverIdx(Math.max(0, Math.min(n - 1, Math.round(raw))));
  };

  const yTicks = [0, 0.5, 1].map(pct => ({
    val: Math.round(yMax * pct),
    y:   yOf(yMax * pct),
  }));

  const interval = n <= 15 ? 2 : 5;
  const xTicks = n > 0 ? labels.map((lbl, i) => {
    if (i !== 0 && i !== n - 1 && i % interval !== 0) return null;
    const d = new Date(lbl + "T12:00:00Z");
    return { i, x: xOf(i), label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) };
  }).filter(Boolean) : [];

  const tooltip = hoverIdx !== null && labels ? {
    date: new Date(labels[hoverIdx] + "T12:00:00Z").toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric",
    }),
    x: xOf(hoverIdx),
    items: SERIES_CONFIG.map(({ key, label, color }) => ({
      key, label, color, value: (series?.[key] || [])[hoverIdx] || 0,
    })),
  } : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: "200px" }}>
        <div className="w-5 h-5 rounded-full border-2 border-blue-dark border-opacity-20 border-t-blue-dark animate-spin" />
      </div>
    );
  }

  if (!labels || n === 0) return null;

  const xPct = tooltip ? Math.min((tooltip.x / VW) * 100, 72) : 0;

  return (
    <div className="relative select-none">
      {/* Legend */}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mb-4 px-1">
        {SERIES_CONFIG.map(({ key, label, color }) => {
          const total = (series?.[key] || []).reduce((a, b) => a + b, 0);
          return (
            <div key={key} className="flex items-center gap-2">
              <span className="w-5 h-[2.5px] rounded-full inline-block" style={{ backgroundColor: color }} />
              <span className="font-cinzel uppercase text-[9px] tracking-wider" style={{ color: "rgba(1,46,114,0.45)" }}>
                {label}
              </span>
              <span className="font-cinzel text-[10px] font-bold" style={{ color }}>{total}</span>
            </div>
          );
        })}
      </div>

      {/* SVG */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VW} ${VH}`}
        className="w-full"
        style={{ height: "200px" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <defs>
          {SERIES_CONFIG.map(({ key, color }) => (
            <linearGradient key={key} id={`dash-grad-${key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={color} stopOpacity="0.14" />
              <stop offset="100%" stopColor={color} stopOpacity="0.01" />
            </linearGradient>
          ))}
        </defs>

        {/* Y grid + labels */}
        {yTicks.map(({ val, y }) => (
          <g key={val}>
            <line x1={PAD.left} y1={y.toFixed(1)} x2={VW - PAD.right} y2={y.toFixed(1)}
              stroke="rgba(1,46,114,0.07)" strokeWidth="1"
              strokeDasharray={val === 0 ? "none" : "3,5"} />
            <text x={PAD.left - 6} y={y + 3.5} textAnchor="end" fill="rgba(1,46,114,0.28)"
              style={{ fontSize: "9px", fontFamily: "'Cinzel',serif" }}>
              {val}
            </text>
          </g>
        ))}

        {/* X axis labels */}
        {xTicks.map(({ i, x, label }) => (
          <text key={i} x={x} y={VH - 6} textAnchor="middle" fill="rgba(1,46,114,0.28)"
            style={{ fontSize: "8px", fontFamily: "'Cinzel',serif" }}>
            {label}
          </text>
        ))}

        {/* Area fills */}
        {SERIES_CONFIG.map(({ key }) => (
          <path key={`area-${key}`} d={areaD(series?.[key] || [])}
            fill={`url(#dash-grad-${key})`} />
        ))}

        {/* Lines */}
        {SERIES_CONFIG.map(({ key, color }) => (
          <path key={`line-${key}`} d={pathD(series?.[key] || [])}
            fill="none" stroke={color} strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
        ))}

        {/* Hover guide + dots */}
        {hoverIdx !== null && (
          <>
            <line x1={xOf(hoverIdx).toFixed(1)} y1={PAD.top}
              x2={xOf(hoverIdx).toFixed(1)} y2={PAD.top + plotH}
              stroke="rgba(1,46,114,0.12)" strokeWidth="1" strokeDasharray="3,3" />
            {SERIES_CONFIG.map(({ key, color }) => {
              const v = (series?.[key] || [])[hoverIdx] || 0;
              return (
                <circle key={key} cx={xOf(hoverIdx).toFixed(1)} cy={yOf(v).toFixed(1)}
                  r="4" fill={color} stroke="white" strokeWidth="2" />
              );
            })}
          </>
        )}
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div className="absolute pointer-events-none bg-white rounded-xl shadow-lg border border-blue-dark border-opacity-10 px-4 py-3 z-10"
          style={{ left: `${xPct}%`, top: "40px", transform: "translateX(-50%)", minWidth: "155px" }}>
          <p className="font-cinzel uppercase text-[8px] tracking-widest mb-2"
            style={{ color: "rgba(1,46,114,0.35)" }}>
            {tooltip.date}
          </p>
          {tooltip.items.map(({ key, label, color, value }) => (
            <div key={key} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color, display: "inline-block" }} />
                <span className="font-cinzel uppercase text-[8px] tracking-wide"
                  style={{ color: "rgba(1,46,114,0.45)" }}>{label}</span>
              </div>
              <span className="font-cinzel text-[11px] font-bold" style={{ color }}>{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Login Log Table ───────────────────────────────────────────────────
function LoginLogTable({ logs, loading, isSuperAdmin }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="w-5 h-5 rounded-full border-2 border-blue-dark border-opacity-20 border-t-blue-dark animate-spin" />
      </div>
    );
  }

  if (!logs.length) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-2">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.2)" strokeWidth="1.5" strokeLinecap="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <p className="font-constantia text-gray-400 text-sm">No login activity recorded yet.</p>
        <p className="font-constantia text-gray-400 text-xs">Activity will appear here after the next login.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-white-warm bg-opacity-80">
            {["Admin", "Role", "IP Address", "Time", "Status"].map(h => (
              <th key={h} className="px-5 py-3 text-left font-cinzel uppercase text-gray-400 text-[9px] tracking-[0.15em]">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {logs.map((log, i) => (
            <tr key={log._id}
              className="border-t border-gray-50 hover:bg-blue-dark hover:bg-opacity-[0.02] transition-colors duration-100"
              style={{ animation: "row-fade-in 0.3s ease-out both", animationDelay: `${i * 20}ms` }}>
              <td className="px-5 py-3">
                {isSuperAdmin ? (
                  <div>
                    <span className="font-constantia text-blue-dark text-sm block">
                      {log.name || log.username}
                    </span>
                    {log.username && log.name && log.username !== "—" && (
                      <span className="font-constantia text-gray-400 text-xs">@{log.username}</span>
                    )}
                  </div>
                ) : (
                  <span className="font-constantia text-blue-dark text-sm">You</span>
                )}
              </td>
              <td className="px-5 py-3">
                <span className={`font-cinzel uppercase text-[8px] tracking-widest px-2 py-1 rounded-full ${
                  log.role === "super-admin"
                    ? "bg-blue-100 text-blue-700"
                    : log.role === "unknown"
                    ? "bg-gray-100 text-gray-400"
                    : "bg-slate-100 text-slate-600"
                }`}>
                  {log.role === "super-admin" ? "Super" : log.role === "unknown" ? "—" : "Admin"}
                </span>
              </td>
              <td className="px-5 py-3">
                <span className="font-mono text-[11px] text-gray-400">{log.ip || "—"}</span>
              </td>
              <td className="px-5 py-3 whitespace-nowrap">
                <span className="font-constantia text-gray-500 text-xs">{timeAgo(log.createdAt)}</span>
              </td>
              <td className="px-5 py-3">
                {log.success ? (
                  <span className="font-cinzel uppercase text-[8px] tracking-widest px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">
                    ✓ Success
                  </span>
                ) : (
                  <div>
                    <span className="font-cinzel uppercase text-[8px] tracking-widest px-2.5 py-1 rounded-full bg-red-50 text-red-600">
                      ✗ Failed
                    </span>
                    {log.reason && (
                      <span className="font-constantia text-[10px] text-gray-400 ml-2">{log.reason}</span>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────
export default function AdminDashboard() {
  usePageTitle("Dashboard");
  const navigate = useNavigate();
  const [stats,       setStats]       = useState(null);
  const [recent,      setRecent]      = useState([]);
  const [chartData,   setChartData]   = useState(null);
  const [loginLogs,   setLoginLogs]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [logsLoading,  setLogsLoading]  = useState(true);
  const [error,       setError]       = useState(null);
  const [mounted,     setMounted]     = useState(false);

  const adminPayload = getAdminPayload();
  const isSuperAdmin = adminPayload?.role === "super-admin";

  useEffect(() => {
    async function load() {
      try {
        const [quotesRes, consultationsRes, contactsRes, serviceRes, recentRes] = await Promise.all([
          adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/quotes`),
          adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/consultations`),
          adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/contacts`),
          adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/service-requests`),
          adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/recent`),
        ]);

        if ([quotesRes, consultationsRes, contactsRes, serviceRes, recentRes].some(r => r.status === 401)) {
          localStorage.removeItem("paladin_admin_token");
          navigate("/admin/login");
          return;
        }

        if (!quotesRes.ok || !consultationsRes.ok || !contactsRes.ok || !serviceRes.ok || !recentRes.ok) {
          throw new Error("Failed to load dashboard data.");
        }

        const [quotesJson, consultationsJson, contactsJson, serviceJson, recentJson] = await Promise.all([
          quotesRes.json(),
          consultationsRes.json(),
          contactsRes.json(),
          serviceRes.json(),
          recentRes.json(),
        ]);

        const quotes        = quotesJson.data        || [];
        const consultations = consultationsJson.data || [];
        const contacts      = contactsJson.data      || [];
        const serviceReqs   = serviceJson.data       || [];

        const quotePending           = quotes.filter(q => q.status === "pending").length;
        const consultationPending    = consultations.filter(c => c.status === "pending").length;
        const contactUnread          = contacts.filter(m => m.status === "unread").length;
        const serviceRequestPending  = serviceReqs.filter(s => s.status === "pending").length;
        const totalPending           = quotePending + consultationPending + contactUnread + serviceRequestPending;

        setStats({
          quotes:               quotes.length,
          consultations:        consultations.length,
          contacts:             contacts.length,
          serviceRequests:      serviceReqs.length,
          quotePending,
          consultationPending,
          contactUnread,
          serviceRequestPending,
          totalPending,
        });

        setRecent(recentJson.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
        setTimeout(() => setMounted(true), 50);
      }
    }

    async function loadChart() {
      try {
        const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/stats/chart`);
        if (res.ok) {
          const json = await res.json();
          if (json.success) setChartData(json);
        }
      } catch {}
      finally { setChartLoading(false); }
    }

    async function loadLogs() {
      try {
        const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/login-logs?limit=30`);
        if (res.ok) {
          const json = await res.json();
          if (json.success) setLoginLogs(json.logs || []);
        }
      } catch {}
      finally { setLogsLoading(false); }
    }

    load();
    loadChart();
    loadLogs();
  }, [navigate]);

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  if (loading) return <Spinner />;

  if (error) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "240px", gap: "12px" }}>
      <p style={{ fontFamily: "'Constantia', serif", color: "#dc2626", fontSize: "14px" }}>{error}</p>
      <button onClick={() => window.location.reload()} style={{
        fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase",
        color: "#012E72", border: "1px solid rgba(1,46,114,0.25)", borderRadius: "999px",
        padding: "8px 20px", cursor: "pointer", backgroundColor: "transparent",
      }}>Retry</button>
    </div>
  );

  const STAT_CARDS = [
    {
      label: "Quotes",
      total: stats.quotes,
      pending: stats.quotePending,
      pendingLabel: "pending",
      color: "#002DB5",
      bg: "rgba(0,45,181,0.09)",
      path: "/admin/quotes",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      ),
    },
    {
      label: "Consultations",
      total: stats.consultations,
      pending: stats.consultationPending,
      pendingLabel: "pending",
      color: "#012E72",
      bg: "rgba(1,46,114,0.09)",
      path: "/admin/consultations",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      label: "Contact Messages",
      total: stats.contacts,
      pending: stats.contactUnread,
      pendingLabel: "unread",
      color: "#065F46",
      bg: "rgba(6,95,70,0.09)",
      path: "/admin/contacts",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      ),
    },
    {
      label: "Service Requests",
      total: stats.serviceRequests,
      pending: stats.serviceRequestPending,
      pendingLabel: "pending",
      color: "#92400E",
      bg: "rgba(146,64,14,0.09)",
      path: "/admin/service-requests",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 11 12 14 22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      ),
    },
  ];

  const totalAll = stats.quotes + stats.consultations + stats.contacts + stats.serviceRequests;

  // Build date range label for chart
  const chartRange = chartData?.labels?.length
    ? (() => {
        const first = new Date(chartData.labels[0] + "T12:00:00Z");
        const last  = new Date(chartData.labels[chartData.labels.length - 1] + "T12:00:00Z");
        const fmt = { month: "short", day: "numeric" };
        return `${first.toLocaleDateString("en-US", fmt)} – ${last.toLocaleDateString("en-US", fmt)}`;
      })()
    : "";

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: "22px" }} className="max-w-6xl w-full">

        {/* ── Header ── */}
        <div className={`flex flex-col sm:flex-row sm:items-end justify-between gap-2 transition-all duration-500 ease-out ${mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}>
          <div>
            <p style={{
              fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.26em",
              textTransform: "uppercase", color: "rgba(1,46,114,0.35)", marginBottom: "6px",
            }}>
              Admin / Dashboard
            </p>
            <h1 style={{
              fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "24px",
              letterSpacing: "0.04em", color: "#012E72", lineHeight: 1,
            }}>
              Dashboard
            </h1>
            <p style={{
              fontFamily: "'Constantia', serif", fontSize: "13px",
              color: "rgba(1,46,114,0.4)", marginTop: "6px",
            }}>
              {today}
            </p>
          </div>
          <p style={{
            fontFamily: "'Constantia', serif", fontSize: "13px",
            color: "rgba(1,46,114,0.4)", fontStyle: "italic", paddingBottom: "2px",
          }}>
            {totalAll} total submission{totalAll !== 1 ? "s" : ""} across all collections
          </p>
        </div>

        {/* ── Gradient divider ── */}
        <div className={`h-px bg-gradient-to-r from-blue-dark via-blue-dark to-transparent opacity-10 transition-all duration-700 ease-out ${mounted ? "opacity-10 scale-x-100" : "opacity-0 scale-x-0"} origin-left`} />

        {/* ── Pending alert ── */}
        {stats.totalPending > 0 && (
          <div
            className={`flex items-start gap-4 px-5 py-4 rounded-xl border-l-4 bg-amber-50 border-amber-400 transition-all duration-500 ease-out ${mounted ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"}`}
            style={{ transitionDelay: "100ms" }}
          >
            <div className="flex-shrink-0 mt-0.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-wiggle">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <p className="font-constantia text-amber-800 text-sm leading-relaxed">
              <span className="font-cinzel font-bold text-[11px] uppercase tracking-widest mr-1">{stats.totalPending} items</span>
              need your attention —
              {stats.quotePending > 0          && <> <strong>{stats.quotePending}</strong> quote{stats.quotePending !== 1 ? "s" : ""},</>}
              {stats.consultationPending > 0   && <> <strong>{stats.consultationPending}</strong> consultation{stats.consultationPending !== 1 ? "s" : ""},</>}
              {stats.contactUnread > 0         && <> <strong>{stats.contactUnread}</strong> unread message{stats.contactUnread !== 1 ? "s" : ""},</>}
              {stats.serviceRequestPending > 0 && <> <strong>{stats.serviceRequestPending}</strong> service request{stats.serviceRequestPending !== 1 ? "s" : ""},</>}
            </p>
          </div>
        )}

        {/* ── Stat cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {STAT_CARDS.map((card, i) => (
            <StatCard
              key={card.label}
              {...card}
              delay={150 + i * 80}
              onClick={() => navigate(card.path)}
            />
          ))}
        </div>

        {/* ── 30-Day Trend Chart ── */}
        <div
          className={`bg-white rounded-2xl overflow-hidden border border-blue-dark border-opacity-[0.08] transition-all duration-500 ease-out ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ transitionDelay: "380ms" }}
        >
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white-warm bg-opacity-60">
            <div className="flex items-center gap-2.5">
              <div className="w-1 h-4 rounded-full bg-blue-dark opacity-30" />
              <h3 className="font-cinzel font-bold text-blue-dark uppercase text-[11px] tracking-[0.15em]">
                30-Day Submissions Trend
              </h3>
            </div>
            {chartRange && (
              <span className="font-constantia text-gray-400 italic text-xs">{chartRange}</span>
            )}
          </div>
          <div className="px-6 py-5">
            <TrendChart
              labels={chartData?.labels}
              series={chartData?.series}
              loading={chartLoading}
            />
          </div>
        </div>

        {/* ── Recent activity ── */}
        <div
          className={`bg-white rounded-2xl overflow-hidden border border-blue-dark border-opacity-[0.08] transition-all duration-500 ease-out ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ transitionDelay: "500ms" }}
        >
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white-warm bg-opacity-60">
            <div className="flex items-center gap-2.5">
              <div className="w-1 h-4 rounded-full bg-blue-dark opacity-30" />
              <h3 className="font-cinzel font-bold text-blue-dark uppercase text-[11px] tracking-[0.15em]">
                Recent Activity
              </h3>
            </div>
            <span className="font-constantia text-gray-400 italic text-xs">
              Last {recent.length} submissions
            </span>
          </div>

          {recent.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
              <div className="w-14 h-14 rounded-full bg-blue-dark bg-opacity-5 flex items-center justify-center">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <p className="font-constantia text-gray-400 text-sm">No submissions yet.</p>
            </div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-white-warm bg-opacity-80">
                      {["Type", "Name", "Email", "Detail", "Date", "Status"].map((h) => (
                        <th key={h} className="px-5 py-3 text-left font-cinzel uppercase text-gray-400 text-[9px] tracking-[0.15em]">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((item, i) => (
                      <tr
                        key={item._id}
                        onClick={() => navigate(TYPE_CONFIG[item._type]?.path || "/admin")}
                        className="group cursor-pointer border-t border-gray-50 hover:bg-blue-dark hover:bg-opacity-[0.03] transition-all duration-150"
                        style={{ animationDelay: `${600 + i * 40}ms`, animation: "row-fade-in 0.4s ease-out both" }}
                      >
                        <td className="px-5 py-3.5 relative">
                          <div className="absolute left-0 top-0 bottom-0 w-0.5 scale-y-0 group-hover:scale-y-100 transition-transform duration-200 origin-center rounded-r"
                            style={{ backgroundColor: TYPE_CONFIG[item._type]?.color || "#012E72" }} />
                          <TypeBadge type={item._type} />
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="font-constantia text-blue-dark text-sm">{item.displayName}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="font-constantia text-gray-500 text-[13px]">{item.email}</span>
                        </td>
                        <td className="px-5 py-3.5 max-w-[180px]">
                          <span className="font-constantia text-gray-400 truncate block text-xs">{formatSubType(item.sub)}</span>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <span className="font-constantia text-gray-400 text-xs">{timeAgo(item.createdAt)}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <StatusBadge status={item.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden flex flex-col divide-y divide-gray-50">
                {recent.map((item, i) => (
                  <div
                    key={item._id}
                    onClick={() => navigate(TYPE_CONFIG[item._type]?.path || "/admin")}
                    className="relative px-5 py-4 cursor-pointer hover:bg-blue-dark hover:bg-opacity-[0.03] transition-colors duration-150 overflow-hidden"
                    style={{ animationDelay: `${600 + i * 40}ms`, animation: "row-fade-in 0.4s ease-out both" }}
                  >
                    <div className="absolute left-0 top-0 bottom-0 w-0.5"
                      style={{ backgroundColor: TYPE_CONFIG[item._type]?.color || "#012E72", opacity: 0.4 }} />
                    <div className="flex items-center justify-between mb-1.5">
                      <TypeBadge type={item._type} />
                      <StatusBadge status={item.status} />
                    </div>
                    <p className="font-constantia text-blue-dark text-sm">{item.displayName}</p>
                    <p className="font-constantia text-gray-400 text-xs mt-0.5">
                      {item.email} · {timeAgo(item.createdAt)}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── Login Activity ── */}
        <div
          className={`bg-white rounded-2xl overflow-hidden border border-blue-dark border-opacity-[0.08] transition-all duration-500 ease-out ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ transitionDelay: "620ms" }}
        >
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-white-warm bg-opacity-60">
            <div className="flex items-center gap-2.5">
              <div className="w-1 h-4 rounded-full bg-blue-dark opacity-30" />
              <h3 className="font-cinzel font-bold text-blue-dark uppercase text-[11px] tracking-[0.15em]">
                Login Activity
              </h3>
            </div>
            <span className="font-constantia text-gray-400 italic text-xs">
              {isSuperAdmin ? "All admins · last 30 entries" : "Your sessions · last 30 entries"}
            </span>
          </div>
          <LoginLogTable logs={loginLogs} loading={logsLoading} isSuperAdmin={isSuperAdmin} />
        </div>

      </div>

      {/* ── Animation keyframes ── */}
      <style>{`
        @keyframes row-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          20%       { transform: rotate(-8deg); }
          40%       { transform: rotate(8deg); }
          60%       { transform: rotate(-4deg); }
          80%       { transform: rotate(4deg); }
        }
        @keyframes pulse-soft {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.6; }
        }
        .animate-wiggle     { animation: wiggle 1s ease-in-out 0.8s both; }
        .animate-pulse-soft { animation: pulse-soft 2s ease-in-out infinite; }
      `}</style>
    </>
  );
}
